import React from "react";
// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Senthil Solar React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Senthil Solar React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Icons
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import VerifiedIcon from "@mui/icons-material/Verified";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images - Use an existing image or add 'commercial_solar_bg.jpg' to src/assets/images/
// For now, reusing 'canva_banner.png' from Awards as a placeholder; replace with your commercial bg
import bgImage from "assets/images/canva_banner.png";

// Animation imports
import { useSpring, animated, useTrail } from 'react-spring';
import { useInView } from 'react-intersection-observer';

// Create animated versions of MUI components
const AnimatedCard = animated(Card);
const AnimatedGrid = animated(Grid);
const AnimatedMKBox = animated(MKBox);

function CommercialService() {
  // For header animation
  const [headerRef, headerInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });
  
  const headerAnimation = useSpring({
    opacity: headerInView ? 1 : 0,
    transform: headerInView ? 'translateY(0)' : 'translateY(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  // For features section animation
  const [featuresRef, featuresInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });

  const featuresHeaderAnimation = useSpring({
    opacity: featuresInView ? 1 : 0,
    transform: featuresInView ? 'translateY(0)' : 'translateY(30px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  // Staggered animation for features cards
  const features = [
    {
      icon: <TrendingDownIcon />,
      title: "Reduce Operating Costs",
      description: "Lower your commercial electricity expenses by 70-90% and improve profit margins significantly."
    },
    {
      icon: <VerifiedIcon />,
      title: "Tax Benefits & Incentives",
      description: "Accelerated depreciation up to 40% in the first year plus various state and central government incentives."
    },
    {
      icon: <SupportAgentIcon />,
      title: "Dedicated Support",
      description: "24/7 monitoring, preventive maintenance, and quick response team for uninterrupted operations."
    }
  ];

  const [cardsRef, cardsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const cardTrail = useTrail(features.length, {
    opacity: cardsInView ? 1 : 0,
    transform: cardsInView ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.95)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For sectors section
  const [sectorsRef, sectorsInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const sectorsAnimation = useSpring({
    opacity: sectorsInView ? 1 : 0,
    transform: sectorsInView ? 'translateY(0)' : 'translateY(50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const sectors = [
    "Manufacturing Units",
    "IT Parks & Offices",
    "Hotels & Resorts",
    "Educational Institutions",
    "Hospitals & Healthcare",
    "Shopping Malls",
    "Warehouses",
    "Industrial Complexes"
  ];

  // Staggered for sectors
  const [sectorsItemsRef, sectorsItemsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const sectorsTrail = useTrail(sectors.length, {
    opacity: sectorsItemsInView ? 1 : 0,
    transform: sectorsItemsInView ? 'scale(1)' : 'scale(0.8)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For turnkey section
  const [turnkeyRef, turnkeyInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const turnkeyAnimation = useSpring({
    opacity: turnkeyInView ? 1 : 0,
    transform: turnkeyInView ? 'translateX(0)' : 'translateX(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  // For CTA buttons
  const [ctaRef, ctaInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const ctaAnimation = useSpring({
    opacity: ctaInView ? 1 : 0,
    transform: ctaInView ? 'translateY(0)' : 'translateY(30px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });


  return (
    <>
      <DefaultNavbar
        routes={routes}
        transparent
        light
      />
      <MKBox
        minHeight="60vh"
        width="100%"
        sx={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container sx={{mt:6}}>
          <AnimatedGrid 
            ref={headerRef}
            style={headerAnimation}
            container 
            item 
            xs={12} 
            lg={7} 
            justifyContent="center" 
            mx="auto"
          >
            <MKTypography
              variant="h1"
              color="white"
              mt={-6}
              mb={1}
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
                textAlign: "center",
              })}
            >
              Commercial Solar
              <br />
              Installations
            </MKTypography>
            <MKTypography
              variant="h5"
              color="white"
              textAlign="center"
              sx={{ opacity: 0.9 }}
            >
              Power Your Business with Sustainable Energy Solutions
            </MKTypography>
          </AnimatedGrid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={6}>
          <Container>
            <Grid container item xs={12} lg={8} mx="auto" mb={4}>
              <MKTypography variant="body1" color="text" textAlign="center">
                Senthil Solar specializes in large-scale commercial and industrial solar installations. 
                With over 500+ commercial projects completed, we understand the unique energy demands of 
                businesses and design systems that maximize ROI while ensuring reliable power supply. 
                Our commercial solutions range from 10kW to multi-MW installations, suitable for enterprises 
                of all sizes.
              </MKTypography>
            </Grid>
            
            {/* Key Advantages Section */}
            <AnimatedGrid 
              ref={featuresRef}
              style={featuresHeaderAnimation}
              container 
              item 
              xs={12} 
              justifyContent="center" 
              mx="auto" 
              mb={4}
            >
              <MKTypography variant="h4" textAlign="center" mb={3}>
                Key Advantages
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={3} ref={cardsRef}>
              {cardTrail.map((style, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <AnimatedCard style={style} sx={{ p: 3, height: "100%", textAlign: "center" }}>
                    <MKBox color="info.main" mb={2}>
                      {React.cloneElement(features[index].icon, { fontSize: "large" })}
                    </MKBox>
                    <MKTypography variant="h5" mb={1}>
                      {features[index].title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {features[index].description}
                    </MKTypography>
                  </AnimatedCard>
                </Grid>
              ))}
            </Grid>

            {/* Industries We Serve Section */}
            <AnimatedGrid 
              ref={sectorsRef}
              style={sectorsAnimation}
              container 
              item 
              xs={12} 
              md={10} 
              lg={8} 
              justifyContent="center" 
              mx="auto" 
              mt={6}
            >
              <MKTypography variant="h4" textAlign="center" mb={3}>
                Industries We Serve
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={2} ref={sectorsItemsRef}>
              {sectorsTrail.map((style, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <animated.div style={style}>
                    <MKBox 
                      p={2} 
                      textAlign="center" 
                      borderRadius="lg"
                      sx={{ 
                        backgroundColor: "rgba(33, 150, 243, 0.1)",
                        border: "1px solid rgba(33, 150, 243, 0.3)",
                        transition: "transform 0.3s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-5px)",
                        },
                      }}
                    >
                      <MKTypography variant="button" fontWeight="medium">
                        {sectors[index]}
                      </MKTypography>
                    </MKBox>
                  </animated.div>
                </Grid>
              ))}
            </Grid>

            {/* Complete Turnkey Solutions Section */}
            <AnimatedMKBox 
              ref={turnkeyRef}
              style={turnkeyAnimation}
              component="section" 
              mb={6} 
              mt={6}
              p={4} 
              borderRadius="lg" 
              sx={{ backgroundColor: "rgba(76, 175, 80, 0.05)" }}
            >
              <MKTypography variant="h4" mb={2}>
                Complete Turnkey Solutions
              </MKTypography>
              <MKTypography variant="body1" color="text">
                From initial feasibility studies to post-installation O&M, we handle everything. 
                Our services include energy audit, custom system design, MNRE subsidy processing, 
                net metering approvals, EPC services, performance monitoring systems, and 
                comprehensive AMC packages. We ensure zero downtime with our proactive 
                maintenance approach and rapid response teams.
              </MKTypography>
            </AnimatedMKBox>

            {/* CTA Buttons */}
            <AnimatedGrid 
              ref={ctaRef}
              style={ctaAnimation}
              container 
              item 
              xs={12} 
              justifyContent="center" 
              mx="auto"
            >
            </AnimatedGrid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default CommercialService;